import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { ArrowLeft, Edit, Plus, Printer, Save, Star, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ICS224Form = ({ currentExercise, theme }) => {
  const [view, setView] = useState('list');
  const [ratings, setRatings] = useState([]);
  const [editingRating, setEditingRating] = useState(null);
  const [formData, setFormData] = useState({
    exercise_name: '',
    crew_name_number: '',
    crew_leader: '',
    crew_home_unit: '',
    address: '',
    location_of_incident: '',
    agency_representative: '',
    date_from: '',
    date_to: '',
    number_of_shifts_worked: '',
    // Rating factors
    physical_condition: '',
    hot_line_construction: '',
    mop_up: '',
    off_line: '',
    use_of_safe_practices: '',
    crew_organization: '',
    equipment: '',
    other_1: '',
    other_1_rating: '',
    other_2: '',
    other_2_rating: '',
    other_3: '',
    other_3_rating: '',
    supervisory_crew_leader: '',
    supervisory_squad_boss: '',
    areas_needing_performance: '',
    outstanding_workers: '',
    individuals_needing_improvement: '',
    remarks: '',
    crew_leader_name: '',
    crew_leader_date: '',
    rated_by: '',
    rated_by_date: ''
  });

  const API = API_BASE_URL || 'http://localhost:8001';

  // Fetch crew performance ratings
  useEffect(() => {
    const fetchRatings = async () => {
      if (currentExercise?.id) {
        try {
          const response = await axios.get(`${API}/api/crew-performance-ratings?exercise_id=${currentExercise.id}`);
          setRatings(response.data);
        } catch (error) {
          console.error('Error fetching crew performance ratings:', error);
        }
      }
    };
    fetchRatings();
  }, [currentExercise, API]);

  // Set exercise name when currentExercise changes
  useEffect(() => {
    if (currentExercise?.exercise_name) {
      setFormData(prev => ({
        ...prev,
        exercise_name: currentExercise.exercise_name
      }));
    }
  }, [currentExercise]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      exercise_name: currentExercise?.exercise_name || '',
      crew_name_number: '',
      crew_leader: '',
      crew_home_unit: '',
      address: '',
      location_of_incident: '',
      agency_representative: '',
      date_from: '',
      date_to: '',
      number_of_shifts_worked: '',
      physical_condition: '',
      hot_line_construction: '',
      mop_up: '',
      off_line: '',
      use_of_safe_practices: '',
      crew_organization: '',
      equipment: '',
      other_1: '',
      other_1_rating: '',
      other_2: '',
      other_2_rating: '',
      other_3: '',
      other_3_rating: '',
      supervisory_crew_leader: '',
      supervisory_squad_boss: '',
      areas_needing_performance: '',
      outstanding_workers: '',
      individuals_needing_improvement: '',
      remarks: '',
      crew_leader_name: '',
      crew_leader_date: '',
      rated_by: '',
      rated_by_date: ''
    });
  };

  const handleSave = async () => {
    const ratingData = {
      id: editingRating ? editingRating.id : Date.now(),
      ...formData,
      exercise_id: currentExercise?.id,
      created_at: editingRating ? editingRating.created_at : new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    try {
      if (editingRating) {
        await axios.put(`${API}/api/crew-performance-ratings/${editingRating.id}`, ratingData);
        setRatings(prev => prev.map(item => item.id === editingRating.id ? ratingData : item));
      } else {
        const response = await axios.post(`${API}/api/crew-performance-ratings`, ratingData);
        setRatings(prev => [...prev, response.data]);
      }
      setView('list');
      setEditingRating(null);
      resetForm();
    } catch (error) {
      console.error('Error saving crew performance rating:', error);
    }
  };

  const handleEdit = (rating) => {
    setEditingRating(rating);
    setFormData(rating);
    setView('add');
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/crew-performance-ratings/${id}`);
      setRatings(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error deleting crew performance rating:', error);
    }
  };

  const printICS224 = (ratingArg) => {
    const ratingToPrint = ratingArg
      || (view === 'add' ? formData : null)
      || editingRating
      || (ratings.length > 0 ? ratings[0] : formData);

    if (!ratingToPrint || (!ratingToPrint.crew_name_number && !ratingToPrint.crew_leader && !ratingToPrint.id)) {
      alert('No crew performance rating data to print. Please create or select a rating first.');
      return;
    }

    // Renders a filled or empty circle (radio-button style) for print clarity.
    // Uses CSS class (not inline style) because DOMPurify strips style attributes.
    const radioMark = (isSelected) =>
      `<span class="${isSelected ? 'rb rb-on' : 'rb'}"></span>`;
    const printContent = `
      <html>
        <head>
          <title>ICS 224 - Crew Performance Rating</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .form-section { margin-bottom: 20px; }
            .form-row { display: flex; gap: 20px; margin-bottom: 10px; }
            .form-field { flex: 1; }
            .label { font-weight: bold; margin-bottom: 5px; }
            .value { border-bottom: 1px solid #000; min-height: 20px; padding: 2px; }
            .section-title { color: #f97316; font-weight: bold; font-size: 18px; margin: 20px 0 10px 0; }
            .rating-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            .rating-table th, .rating-table td { border: 1px solid #000; padding: 8px; text-align: center; }
            .rating-table th { background-color: #f5f5f5; }
            .checkbox { width: 15px; height: 15px; }
            .rb { display: inline-block; width: 12px; height: 12px; border: 1.5px solid #000; border-radius: 50%; vertical-align: middle; background: transparent; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .rb-on { background: #000 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            @media print {
              .rb, .rb-on { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 224 - CREW PERFORMANCE RATING</h1>
          </div>
          
          <div class="form-section">
            <div class="section-title">Header</div>
            <div class="form-row">
              <div class="form-field">
                <div class="label">Exercise Name:</div>
                <div class="value">${ratingToPrint.exercise_name || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Crew Name and Number:</div>
                <div class="value">${ratingToPrint.crew_name_number || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Crew Leader:</div>
                <div class="value">${ratingToPrint.crew_leader || ''}</div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-field">
                <div class="label">Crew Home Unit:</div>
                <div class="value">${ratingToPrint.crew_home_unit || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Address:</div>
                <div class="value">${ratingToPrint.address || ''}</div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-field">
                <div class="label">Location of Incident:</div>
                <div class="value">${ratingToPrint.location_of_incident || ''}</div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-field">
                <div class="label">Agency Representative:</div>
                <div class="value">${ratingToPrint.agency_representative || ''}</div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-field">
                <div class="label">Date/Time From:</div>
                <div class="value">${ratingToPrint.date_from || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Date/Time To:</div>
                <div class="value">${ratingToPrint.date_to || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Number of Shifts Worked:</div>
                <div class="value">${ratingToPrint.number_of_shifts_worked || ''}</div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="section-title">Crew Evaluation</div>
            <table class="rating-table">
              <tr>
                <th>Rating Factors</th>
                <th>Needs to Improve</th>
                <th>Deficient</th>
                <th>Satisfactory</th>
                <th>Excellent</th>
              </tr>
              <tr>
                <td>Physical Condition</td>
                <td>${radioMark(ratingToPrint.physical_condition === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.physical_condition === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.physical_condition === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.physical_condition === 'excellent')}</td>
              </tr>
              <tr>
                <td>Hot Line Construction</td>
                <td>${radioMark(ratingToPrint.hot_line_construction === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.hot_line_construction === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.hot_line_construction === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.hot_line_construction === 'excellent')}</td>
              </tr>
              <tr>
                <td>Mop-up</td>
                <td>${radioMark(ratingToPrint.mop_up === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.mop_up === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.mop_up === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.mop_up === 'excellent')}</td>
              </tr>
              <tr>
                <td>Off-Line</td>
                <td>${radioMark(ratingToPrint.off_line === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.off_line === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.off_line === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.off_line === 'excellent')}</td>
              </tr>
              <tr>
                <td>Use of Safe Practices</td>
                <td>${radioMark(ratingToPrint.use_of_safe_practices === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.use_of_safe_practices === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.use_of_safe_practices === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.use_of_safe_practices === 'excellent')}</td>
              </tr>
              <tr>
                <td>Crew Organization</td>
                <td>${radioMark(ratingToPrint.crew_organization === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.crew_organization === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.crew_organization === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.crew_organization === 'excellent')}</td>
              </tr>
              <tr>
                <td>Equipment</td>
                <td>${radioMark(ratingToPrint.equipment === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.equipment === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.equipment === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.equipment === 'excellent')}</td>
              </tr>
              <tr>
                <td>${ratingToPrint.other_1 || 'Other'}</td>
                <td>${radioMark(ratingToPrint.other_1_rating === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.other_1_rating === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.other_1_rating === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.other_1_rating === 'excellent')}</td>
              </tr>
              <tr>
                <td>${ratingToPrint.other_2 || 'Other'}</td>
                <td>${radioMark(ratingToPrint.other_2_rating === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.other_2_rating === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.other_2_rating === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.other_2_rating === 'excellent')}</td>
              </tr>
              <tr>
                <td>${ratingToPrint.other_3 || 'Other'}</td>
                <td>${radioMark(ratingToPrint.other_3_rating === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.other_3_rating === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.other_3_rating === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.other_3_rating === 'excellent')}</td>
              </tr>
              <tr>
                <td colspan="5" style="text-align: left; font-weight: bold;">Supervisory Performances</td>
              </tr>
              <tr>
                <td>Crew Leader</td>
                <td>${radioMark(ratingToPrint.supervisory_crew_leader === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.supervisory_crew_leader === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.supervisory_crew_leader === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.supervisory_crew_leader === 'excellent')}</td>
              </tr>
              <tr>
                <td>Squad Boss</td>
                <td>${radioMark(ratingToPrint.supervisory_squad_boss === 'needs_improve')}</td>
                <td>${radioMark(ratingToPrint.supervisory_squad_boss === 'deficient')}</td>
                <td>${radioMark(ratingToPrint.supervisory_squad_boss === 'satisfactory')}</td>
                <td>${radioMark(ratingToPrint.supervisory_squad_boss === 'excellent')}</td>
              </tr>
            </table>
            
            <div style="margin: 20px 0;">
              <div class="label">Areas Needing Performance:</div>
              <div class="value" style="min-height: 60px;">${ratingToPrint.areas_needing_performance || ''}</div>
            </div>
            
            <div class="form-row">
              <div class="form-field">
                <div class="label">Names of Outstanding Workers (comment):</div>
                <div class="value" style="min-height: 40px;">${ratingToPrint.outstanding_workers || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Names of Individuals Needing Improvement:</div>
                <div class="value" style="min-height: 40px;">${ratingToPrint.individuals_needing_improvement || ''}</div>
              </div>
            </div>
            
            <div style="margin: 20px 0;">
              <div class="label">Remarks:</div>
              <div class="value" style="min-height: 60px;">${ratingToPrint.remarks || ''}</div>
            </div>
            
            <div class="form-row">
              <div class="form-field">
                <div class="label">Crew Leader Name:</div>
                <div class="value">${ratingToPrint.crew_leader_name || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Date:</div>
                <div class="value">${ratingToPrint.crew_leader_date || ''}</div>
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-field">
                <div class="label">Rated by:</div>
                <div class="value">${ratingToPrint.rated_by || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Date:</div>
                <div class="value">${ratingToPrint.rated_by_date || ''}</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const RatingRadioGroup = ({ name, value, onChange }) => {
    const options = [
      { value: 'needs_improve', label: 'Needs to Improve' },
      { value: 'deficient', label: 'Deficient' },
      { value: 'satisfactory', label: 'Satisfactory' },
      { value: 'excellent', label: 'Excellent' }
    ];

    return (
      <div className="flex gap-6">
        {options.map((option) => (
          <label key={option.value} className="flex items-center gap-2 text-white">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange(e.target.value)}
              className="form-radio"
            />
            <span className="text-xs">{option.label}</span>
          </label>
        ))}
      </div>
    );
  };

  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-2`}>ICS 224 - Crew Performance Rating</h2>
            <p className={`${theme.colors.textMuted}`}>
              Evaluate and document crew performance during incident operations.
            </p>
          </div>
          <Button
            onClick={() => setView('add')}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New ICS 224
          </Button>
        </div>

        {ratings.length === 0 ? (
          <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Star className={`h-12 w-12 ${theme.colors.textMuted} mb-4`} />
              <p className={`${theme.colors.textMuted} text-lg mb-4`}>No crew performance ratings yet</p>
              <Button
                onClick={() => setView('add')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Create First ICS 224
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {ratings.map((rating) => (
              <Card key={rating.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className={`font-semibold ${theme.colors.textPrimary}`}>
                        {rating.crew_name_number || 'Crew Performance Rating'}
                      </h3>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Crew Leader: {rating.crew_leader || 'Not specified'}
                      </p>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Location: {rating.location_of_incident || 'Not specified'}
                      </p>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Created: {new Date(rating.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(rating)}
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printICS224(rating)}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      >
                        <Printer className="h-3 w-3 mr-1" />
                        Print
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(rating.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-2`}>
            {editingRating ? 'Edit' : 'Create'} ICS 224 - Crew Performance Rating
          </h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingRating(null);
              resetForm();
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          <Button
            onClick={() => printICS224()}
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print ICS 224
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Header</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-white">Exercise Name</Label>
                <Input
                  value={formData.exercise_name}
                  onChange={(e) => handleInputChange('exercise_name', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  readOnly
                />
              </div>
              <div>
                <Label className="text-white">Crew Name and Number</Label>
                <Input
                  value={formData.crew_name_number}
                  onChange={(e) => handleInputChange('crew_name_number', e.target.value)}
                  placeholder="Enter crew name and number"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-white">Crew Leader</Label>
                <Input
                  value={formData.crew_leader}
                  onChange={(e) => handleInputChange('crew_leader', e.target.value)}
                  placeholder="Enter crew leader name"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Crew Home Unit</Label>
                <Input
                  value={formData.crew_home_unit}
                  onChange={(e) => handleInputChange('crew_home_unit', e.target.value)}
                  placeholder="Enter crew home unit"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-white">Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter address"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            </div>
            <div>
              <Label className="text-white">Location of Incident</Label>
              <Input
                value={formData.location_of_incident}
                onChange={(e) => handleInputChange('location_of_incident', e.target.value)}
                placeholder="Enter incident location"
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              />
            </div>
            <div>
              <Label className="text-white">Agency Representative</Label>
              <Input
                value={formData.agency_representative}
                onChange={(e) => handleInputChange('agency_representative', e.target.value)}
                placeholder="Enter representative"
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-white">Date/Time From</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.date_from ? formData.date_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_from ? formData.date_from.split('T')[1] : '00:00';
                      handleInputChange('date_from', `${date}T${time}`);
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_from ? formData.date_from.split('T')[1] || '' : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_from ? formData.date_from.split('T')[0] : '';
                        handleInputChange('date_from', `${date}T${value}`);
                      }
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white">Date/Time To</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.date_to ? formData.date_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_to ? formData.date_to.split('T')[1] : '00:00';
                      handleInputChange('date_to', `${date}T${time}`);
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_to ? formData.date_to.split('T')[1] || '' : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_to ? formData.date_to.split('T')[0] : '';
                        handleInputChange('date_to', `${date}T${value}`);
                      }
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white">Number of Shifts Worked</Label>
                <Input
                  type="number"
                  value={formData.number_of_shifts_worked}
                  onChange={(e) => handleInputChange('number_of_shifts_worked', e.target.value)}
                  placeholder="Enter number"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Crew Evaluation Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Crew Evaluation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Rating Factors Table */}
            <div className="space-y-4">
              {[
                { key: 'physical_condition', label: 'Physical Condition' },
                { key: 'hot_line_construction', label: 'Hot Line Construction' },
                { key: 'mop_up', label: 'Mop-up' },
                { key: 'off_line', label: 'Off-Line' },
                { key: 'use_of_safe_practices', label: 'Use of Safe Practices' },
                { key: 'crew_organization', label: 'Crew Organization' },
                { key: 'equipment', label: 'Equipment' }
              ].map((factor) => (
                <div key={factor.key} className="grid grid-cols-5 gap-2 items-center py-2 border-b border-gray-700">
                  <div className="text-white font-medium">{factor.label}</div>
                  <RatingRadioGroup
                    name={factor.key}
                    value={formData[factor.key]}
                    onChange={(value) => handleInputChange(factor.key, value)}
                  />
                </div>
              ))}

              {/* Other Fields */}
              {[1, 2, 3].map((num) => (
                <div key={`other_${num}`} className="grid grid-cols-5 gap-2 items-center py-2 border-b border-gray-700">
                  <div className="flex">
                    <Input
                      value={formData[`other_${num}`]}
                      onChange={(e) => handleInputChange(`other_${num}`, e.target.value)}
                      placeholder="Other (specify)"
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-400 text-sm"
                    />
                  </div>
                  <RatingRadioGroup
                    name={`other_${num}_rating`}
                    value={formData[`other_${num}_rating`]}
                    onChange={(value) => handleInputChange(`other_${num}_rating`, value)}
                  />
                </div>
              ))}

              {/* Supervisory Performances */}
              <div className="text-white font-semibold text-lg mt-6 mb-4">Supervisory Performances</div>
              
              {[
                { key: 'supervisory_crew_leader', label: 'Crew Leader' },
                { key: 'supervisory_squad_boss', label: 'Squad Boss' }
              ].map((supervisor) => (
                <div key={supervisor.key} className="grid grid-cols-5 gap-2 items-center py-2 border-b border-gray-700">
                  <div className="text-white font-medium">{supervisor.label}</div>
                  <RatingRadioGroup
                    name={supervisor.key}
                    value={formData[supervisor.key]}
                    onChange={(value) => handleInputChange(supervisor.key, value)}
                  />
                </div>
              ))}
            </div>

            {/* Areas Needing Performance */}
            <div>
              <Label className="text-white">Areas Needing Performance</Label>
              <Textarea
                value={formData.areas_needing_performance}
                onChange={(e) => handleInputChange('areas_needing_performance', e.target.value)}
                placeholder="Deficiencies or Needs to Improve need to be explained here"
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                rows={4}
              />
            </div>

            {/* Outstanding Workers and Improvement */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Names of Outstanding Workers (comment)</Label>
                <Textarea
                  value={formData.outstanding_workers}
                  onChange={(e) => handleInputChange('outstanding_workers', e.target.value)}
                  placeholder="Enter names and comments"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-white">Names of Individuals Needing Improvement</Label>
                <Textarea
                  value={formData.individuals_needing_improvement}
                  onChange={(e) => handleInputChange('individuals_needing_improvement', e.target.value)}
                  placeholder="Indicate Areas(s) of Improvement"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  rows={3}
                />
              </div>
            </div>

            {/* Remarks */}
            <div>
              <Label className="text-white">Remarks</Label>
              <Textarea
                value={formData.remarks}
                onChange={(e) => handleInputChange('remarks', e.target.value)}
                placeholder="Enter remarks"
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                rows={4}
              />
            </div>

            {/* Signatures */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Crew Leader Name</Label>
                <Input
                  value={formData.crew_leader_name}
                  onChange={(e) => handleInputChange('crew_leader_name', e.target.value)}
                  placeholder="Enter crew leader name"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-white">Date</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.crew_leader_date ? formData.crew_leader_date.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.crew_leader_date ? formData.crew_leader_date.split('T')[1] : '00:00';
                      handleInputChange('crew_leader_date', `${date}T${time}`);
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.crew_leader_date ? formData.crew_leader_date.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.crew_leader_date ? formData.crew_leader_date.split('T')[0] : '';
                        handleInputChange('crew_leader_date', `${date}T${value}`);
                      }
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Rated by</Label>
                <Input
                  value={formData.rated_by}
                  onChange={(e) => handleInputChange('rated_by', e.target.value)}
                  placeholder="Enter rater name"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-white">Date</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.rated_by_date ? formData.rated_by_date.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.rated_by_date ? formData.rated_by_date.split('T')[1] : '00:00';
                      handleInputChange('rated_by_date', `${date}T${time}`);
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.rated_by_date ? formData.rated_by_date.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.rated_by_date ? formData.rated_by_date.split('T')[0] : '';
                        handleInputChange('rated_by_date', `${date}T${value}`);
                      }
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        {/* Save Button Section */}
        <div className="flex justify-end gap-2 mt-6 sticky bottom-0 bg-gray-900/95 p-4 rounded-lg border border-gray-700">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingRating(null);
              resetForm();
            }}
            data-testid="cancel-ics-224-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="save-ics-224-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {editingRating ? 'Update' : 'Save'} ICS 224
          </Button>
        </div>
      </div>
    </div>
  );
};

// ICS 225 - Personnel Performance Rating Form

export default ICS224Form;
